import { useInfiniteQuery } from "@tanstack/react-query";
import type { AuditEvent } from "@websede/contracts";
import { Download } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { Avatar } from "../../components/avatar.js";
import { Button, ErrorNotice, Select } from "../../components/form.js";

const FILTERS = [
  { value: "", key: "admin.audit.filterAll" },
  { value: "auth.", key: "admin.audit.filterAuth" },
  { value: "invite.", key: "admin.audit.filterInvites" },
  { value: "member.", key: "admin.audit.filterMembers" },
  { value: "dns.", key: "admin.audit.filterDns" },
  { value: "order.", key: "admin.audit.filterOrders" },
  { value: "mailbox.", key: "admin.audit.filterMail" },
] as const;

export function AdminAuditPage() {
  const { t, i18n } = useTranslation();
  const [action, setAction] = useState("");
  const query = useInfiniteQuery({
    queryKey: ["audit", action],
    queryFn: ({ pageParam }) =>
      api.audit.list({
        ...(action ? { action } : {}),
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const events = query.data?.pages.flatMap((p) => p.events) ?? [];
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] text-ink">{t("admin.audit.title")}</h1>
          <p className="max-w-2xl text-sm text-ink-2">{t("admin.audit.description")}</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-56">
            <Select value={action} onChange={(e) => setAction(e.target.value)}>
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {t(f.key)}
                </option>
              ))}
            </Select>
          </div>
          <a
            href={api.audit.exportUrl()}
            className="inline-flex h-11 items-center gap-2 rounded-full border border-ink-3/40 px-5 text-sm font-medium text-accent hover:bg-accent-soft/60"
          >
            <Download className="size-4" />
            {t("admin.audit.export")}
          </a>
        </div>
      </div>

      <ErrorNotice error={query.error} />
      <div className="overflow-x-auto rounded-2xl border border-rule bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-rule text-left text-xs text-ink-3">
              <th className="px-4 py-3 font-medium">{t("admin.audit.event")}</th>
              <th className="hidden px-4 py-3 font-medium sm:table-cell">
                {t("admin.audit.actor")}
              </th>
              <th className="hidden px-4 py-3 font-medium md:table-cell">
                {t("admin.audit.target")}
              </th>
              <th className="px-4 py-3 text-right font-medium">{t("admin.audit.when")}</th>
              <th className="hidden px-4 py-3 font-medium lg:table-cell">
                {t("admin.audit.from")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {events.map((event) => (
              <AuditRow key={event.id} event={event} format={format} />
            ))}
          </tbody>
        </table>
        {query.isSuccess && events.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-3">{t("admin.audit.empty")}</p>
        ) : null}
      </div>
      {query.hasNextPage ? (
        <div>
          <Button
            variant="secondary"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {t("admin.audit.loadMore")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function AuditRow({ event, format }: { event: AuditEvent; format: (iso: string) => string }) {
  const { t } = useTranslation();
  const detail = summarizeDetails(event);
  const actor = event.actor?.label || t("admin.audit.system");
  return (
    <tr className="hover:bg-ink/[0.02]">
      <td className="px-4 py-3">
        <div className="text-ink">{t(`admin.audit.actions.${event.action}`)}</div>
        {detail ? <div className="text-xs text-ink-3">{detail}</div> : null}
        <div className="text-xs text-ink-3 sm:hidden">{actor}</div>
      </td>
      <td className="hidden px-4 py-3 sm:table-cell">
        <span className="flex items-center gap-2">
          <Avatar name={actor} size={24} />
          <span className="truncate text-ink-2">{actor}</span>
        </span>
      </td>
      <td className="hidden px-4 py-3 text-ink-2 md:table-cell">
        {event.target?.label ?? event.target?.id ?? "—"}
      </td>
      <td className="px-4 py-3 text-right text-xs whitespace-nowrap text-ink-3">
        {format(event.createdAt)}
      </td>
      <td
        className="hidden max-w-56 truncate px-4 py-3 text-xs text-ink-3 lg:table-cell"
        title={event.userAgent ?? ""}
      >
        {event.ipAddress ?? "—"}
      </td>
    </tr>
  );
}

/** A short, readable rendering of the details object; keys are stable API names. */
function summarizeDetails(event: AuditEvent): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(event.details)) {
    if (value === undefined || value === null || value === "") continue;
    if (key === "credentialId") continue;
    parts.push(`${key}: ${String(value)}`);
  }
  return parts.join(" · ");
}
